import React, {
    ChangeEvent,
    ReactNode,
    useEffect,
    useMemo,
    useState,
} from "react";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import FormHelperText from "@mui/material/FormHelperText";
import { useTranslation } from "react-i18next";
import { Base } from "../../framework/common/base";
import ListSubheader from "@mui/material/ListSubheader";

export interface IMuiSelectOption {
    id: string;
    title: string;
    group?: string;
}

export interface IMuiSelectProps {
    name: string;
    label?: string;
    options: IMuiSelectOption[];
    defaultValue?: string;
    value?: string;
    maxHeight?: number;
    onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    fontSize?: string | number;
    noSelection?: boolean; // "no selection" in options
    disabled?: boolean;
    disabledItems?: string[];
}

export default function MuiSelect({
    name,
    onChange,
    defaultValue,
    value,
    label,
    options,
    maxHeight,
    required,
    error,
    helperText,
    fontSize,
    noSelection,
    disabled,
    disabledItems,
}: IMuiSelectProps) {
    const [selected, setSelected] = useState<string>(
        defaultValue || value || ""
    );
    const { t } = useTranslation();

    const handleChange = (event) => {
        if (value === undefined) {
            setSelected(event.target.value); // Uncontrolled input
        }
        if (onChange) {
            onChange(event as ChangeEvent<HTMLSelectElement>);
        }
    };

    useEffect(() => {
        if (value !== undefined) {
            setSelected(value); // Controlled input
        }
    }, [value]);

    const grouped = useMemo(() => Base.groupArray(options, "group"), [options]);

    return (
        <FormControl fullWidth required={required} error={error}>
            {label ? <InputLabel>{label}</InputLabel> : null}
            <Select
                name={name}
                value={selected || ""}
                label={label}
                onChange={handleChange}
                MenuProps={{ PaperProps: { style: { maxHeight: maxHeight } } }}
                sx={{ fontSize: fontSize }}
                disabled={disabled}
            >
                {noSelection && (
                    <MenuItem value="">{t("common.noSelection")}</MenuItem>
                )}

                {Object.entries(grouped).map(([group, groupOptions]) => {
                    const items: ReactNode[] = [];
                    if (group !== "undefined") {
                        items.push(<ListSubheader>{group}</ListSubheader>);
                    }
                    items.push(
                        ...groupOptions.map((o) => (
                            <MenuItem
                                key={o.id}
                                value={o.id}
                                style={{ fontSize: fontSize || "inherit" }}
                                disabled={disabledItems?.includes(o.id)}
                            >
                                {o.title}
                            </MenuItem>
                        ))
                    );

                    return items;
                })}
            </Select>
            {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
        </FormControl>
    );
}
